import React, {useState} from "react";
import {useMessages} from "i18n";
import {useTheme} from "styled-components";
import type {SponsorOptions, BookableTemplateKey} from "../sponsorBooking";
import {placeholderArtUrls} from "../BackdropTemplatePicker.styles";
import {previewBackdropArt} from "../takeover/backdropArt";
import {SponsorTakeoverArt} from "../takeover/SponsorTakeover";
import {SponsorPoster} from "../takeover/SponsorPoster";
import type {BackdropArt} from "../takeover/backdropArt";
import type {SponsorTakeover} from "../takeoverModel";
import {
	CardGhost,
	PhoneColumn,
	PreviewBadge,
	PreviewStage,
	PreviewStages,
} from "./WallpaperPreview.styles";

/**
 * What the advertiser is actually buying, drawn live (kitecorp/transferify-ui#1868).
 *
 * A wallpaper booking is not a picture behind a card: it is the page, with the advertiser's own words
 * typeset on it in the placement and the colours they chose. The template picker's tile shows the
 * MOTION; this shows the COMPOSITION, and it has to, because five of the six settings in the panel
 * beneath it change nothing a tile can show.
 *
 * It mounts the REAL renderers — `SponsorTakeoverArt` for the picture and ui#1866's `SponsorPoster` for
 * the block of type — rather than a preview-only lookalike, for the reason both template pickers already
 * give: a second implementation is a promise the product does not keep, and it drifts from the real one
 * on the first change to either. The stage supplies the layout variables the recipient page supplies
 * (see `WallpaperPreview.styles.ts`) so the same stylesheet composes correctly inside it.
 *
 * Dumb by mandate (epic #26): a template, the draft's copy and its options in; nothing decided here.
 */
/**
 * The bounded stage BOTH wallpaper previews compose inside (kitecorp/transferify-ui#1869).
 *
 * Extracted from the sponsor preview below rather than copied, because the sender's own page-theme
 * picker shows the same thing and would otherwise re-declare the layout properties
 * `SponsorPoster.styles.ts` reads to find the card's edge. Getting those wrong is not a visible mistake
 * — the poster simply composes against the wrong box — so there must be exactly one of them.
 *
 * The block of type is a RENDER PROP because it needs the stage element: the contrast scrim samples the
 * pixels under the words, and the element only exists after this component has mounted.
 */
export interface WallpaperStageProps {
	/**
	 * The animated art.
	 *
	 * Never absent: kitecorp/transferify-ui#1867 gave the last treatment in the catalogue its renderer,
	 * so `previewBackdropArt` returns something drawable for every template either caller can hold.
	 */
	art: BackdropArt;
	/** The caption over the corner. */
	badge: string;
	/**
	 * Composes at a phone's proportions (390x844) instead of the desktop page's 16:9
	 * (kitecorp/transferify-ui#2143).
	 *
	 * A prop rather than a second component, because everything inside is the SAME: same art, same
	 * poster, same disclosure, same layout properties. Only the box differs, and the box is the whole
	 * point - a wallpaper booking buys one wide file and a phone keeps about a third of its width.
	 */
	phone?: boolean;
	children: (stage: HTMLElement | null) => React.ReactNode;
}

export function WallpaperStage(
	{art, badge, phone = false, children}: WallpaperStageProps,
): React.ReactElement {
	/*
	 * The stage element, held as STATE rather than as a ref: the contrast sampler has to re-run when it
	 * arrives, and a ref's `current` changing schedules nothing — an effect keyed on one would measure a
	 * null once and never sample. Exactly why `PosterCanvas` holds its own block the same way.
	 */
	const [stage, setStage] = useState<HTMLElement | null>(null);
	return (
		<PreviewStage ref={setStage} inert $phone={phone}>
			<SponsorTakeoverArt art={art} drive="sweep"/>
			<CardGhost $phone={phone}/>
			{children(stage)}
			<PreviewBadge>{badge}</PreviewBadge>
		</PreviewStage>
	);
}

export interface WallpaperPreviewProps {
	template: BookableTemplateKey;
	options: SponsorOptions;
	/** The sponsor's uploaded creative, previewed as soon as there is any. */
	imageUrls: readonly string[];
	/**
	 * A VIDEO_LOOP's own creative: the looping video and the still frame shown in its place
	 * (kitecorp/transferify-ui#1867). Both `null` for every treatment that draws pictures, and the video
	 * is `null` for a video treatment until the advertiser's upload finishes — which is the poster-only
	 * composition the recipient's page draws too, rather than a preview-only state.
	 */
	videoUrl: string | null;
	posterUrl: string | null;
	logoUrl: string | null;
	sponsorName: string;
	headline: string;
	tagline: string;
	ctaLabel: string;
	ctaUrl: string;
	/**
	 * The caption over the corner. Defaults to the advertiser's own wording.
	 *
	 * A prop since kitecorp/transferify-ui#1870, because the operator queue mounts this component too
	 * and "Your poster, live" is addressed to the person who wrote the poster, not to the person
	 * reviewing it.
	 */
	badge?: string;
	/**
	 * Also draws the PHONE stage beside the desktop one (kitecorp/transferify-ui#2143).
	 *
	 * Opt-in rather than the component's new default, because this preview is shared. The booking form
	 * asks for it - an advertiser choosing a focal point has to see what the crop costs on the screen
	 * that costs the most - while the operator queue lays its review pane out around one stage and what
	 * a reviewer sees is a decision for that surface rather than a side effect of this one. The
	 * reviewer is not left guessing meanwhile: `sponsorOptionSummary` gives the queue a "Focal point"
	 * row with the same words the advertiser confirmed.
	 */
	phone?: boolean;
}

export function WallpaperPreview({
	template, options, imageUrls, videoUrl, posterUrl, logoUrl, sponsorName, headline, tagline, ctaLabel,
	ctaUrl, badge, phone = false,
}: WallpaperPreviewProps): React.ReactElement {
	const m = useMessages().sponsor.options;
	const {tokens} = useTheme();
	const placeholders = placeholderArtUrls(tokens);
	// The draft's own settings, so the sweep direction the advertiser just picked is the one they watch,
	// and its own two uploads, so a video treatment previews the file rather than a stand-in.
	const art = previewBackdropArt(template, imageUrls, placeholders, options, {videoUrl, posterUrl});

	/*
	 * The poster's own model. `SponsorPoster` reads no template at all — it composes from the options,
	 * the copy and the mark — so what this carries is the copy, and the art beside it is what the
	 * template decided.
	 */
	const takeover: SponsorTakeover = {
		bookingId: "preview",
		template,
		category: "WALLPAPER",
		options,
		logoUrl,
		sponsorName,
		headline,
		// Absent and blank are one tagline, exactly as `toSponsorTakeover` decides for a served booking:
		// the poster must reserve no line for an empty one.
		tagline: tagline.trim() === "" ? null : tagline,
		ctaLabel,
		ctaUrl,
		imageUrls: [...art.imageUrls],
		videoUrl,
		posterUrl,
	};

	/*
	 * ONE element, mounted twice (kitecorp/transferify-ui#2143). The disclosure is anchored to the
	 * STAGE, not the browser viewport: each box is a picture of the recipient's page, so its own corner
	 * is the corner being previewed. A viewport anchor here would leave the picture entirely and pin an
	 * ad label to the booking form's window.
	 *
	 * The poster, the label and the mark keep their own rules on both stages rather than being laid out
	 * twice: `SponsorPoster` composes against the box it is mounted in, and `PosterRegion` sizes its
	 * type against that box's inline size, so a phone stage is a narrower page rather than a shrunken
	 * picture of a wide one.
	 */
	const poster = (composed: boolean) => (stage: HTMLElement | null) =>
		<SponsorPoster takeover={takeover} artLayer={stage} labelAnchor="stage" composed={composed}/>;

	return (
		<PreviewStages>
			{/*
			  * The desktop stage keeps the recipient page's OWN rule: its region is the space beside the
			  * download card, exactly as it is on a real wide page, which is what the whole composition
			  * was reviewed against.
			  */}
			<WallpaperStage art={art} badge={badge ?? m.preview}>{poster(false)}</WallpaperStage>
			{/*
			  * The SAME booking, cropped by a phone's window. It is the second stage rather than a
			  * second cut: the owner's decision of 2026-09-06 is one wide master cropped around the
			  * advertiser's focal point, with no portrait upload and no blurred fill bands, so what this
			  * shows is exactly what a recipient's phone will paint from the file already uploaded.
			  */}
			{phone && (
				<PhoneColumn>
					{/*
					  * COMPOSED, and it has to be: the page rule is a viewport media query and this stage's
					  * width is not the viewport, so inside a desktop booking form a 390px stage would
					  * take the wide branch and leave the copy 24px beside a phone-width card. See
					  * `SponsorPoster.composed`.
					  */}
					<WallpaperStage art={art} badge={m.previewPhone} phone>{poster(true)}</WallpaperStage>
				</PhoneColumn>
			)}
		</PreviewStages>
	);
}
